import { useCallback, useMemo } from 'react'

import { useAppSelector, useAppDispatch } from 'app/state/hooks'
import { BasemapConfig } from 'features/maps/types'
import { RequestStatus } from 'features/common/types'

import { DEFAULT_MAP_INDEX, SPLIT_MAP_INDEX, MapStyleType } from 'features/builder/types'
import {
  basemapConfigSelector,
  currentBasemapConfigWithStyleSelector,
  mapStylesWithLayerGroupsSelector
} from '../model/mapStyles'
import { updateBasemapConfig } from '../state/builderSlice'
import { isMapSplit } from '../state/keplerGlSlice'

export function useBuilderMap() {
  const mapState = useAppSelector((state) => state.keplerGl?.map?.mapState)
  const isSplit = useAppSelector(isMapSplit)

  const longitude = mapState?.longitude || 0
  const latitude = mapState?.latitude || 0
  const zoom = mapState?.zoom || 0
  const height = mapState?.height || 0
  const is3DView = mapState?.dragRotate || false

  return { longitude, latitude, zoom, height, is3DView, isSplit }
}

export function useBasemapSettings() {
  const basemapSettings = useAppSelector((state) => state.builder.basemapSettings)
  const customBasemaps = useAppSelector((state) => state.builder.customBasemaps)
  const mapStylesStatus = useAppSelector((state) => state.builder.mapStylesStatus)

  const isLoading = mapStylesStatus !== RequestStatus.Fulfilled

  return {
    isLoading,
    basemapSettings,
    customBasemaps
  }
}

export function useLegendSettings() {
  const defaultCustomLegendSettings = {
    layers: {},
    expanded: { 0: false, 1: false },
    baseMapSelector: false,
    initialExpanded: undefined
  }

  const legendSettings = useAppSelector((state) => state.builder?.legendSettings) || defaultCustomLegendSettings

  if (legendSettings?.initialExpanded !== undefined) {
    return {
      ...legendSettings,
      initialExpanded: undefined, // The inclusion of initialExpanded pretends to make a "soft-migration" of Old Maps published without the `expanded` property
      expanded: {
        [DEFAULT_MAP_INDEX]: legendSettings.initialExpanded,
        [SPLIT_MAP_INDEX]: legendSettings.initialExpanded
      }
    }
  }

  return legendSettings
}

const MAP_TYPES = {
  [MapStyleType.MAPLIBRE]: 'CARTO basemaps',
  [MapStyleType.GOOGLE_MAPS]: 'Google basemaps',
  [MapStyleType.CUSTOM]: 'Custom basemaps'
}

export function useBasemapStyles() {
  const basemapSettings = useAppSelector((state) => state.builder.basemapSettings)
  const mapStylesStatus = useAppSelector((state) => state.builder.mapStylesStatus)

  const isLoading = mapStylesStatus !== RequestStatus.Fulfilled

  const mapStyles = useAppSelector(mapStylesWithLayerGroupsSelector)

  const currentMapInitialConfig = useAppSelector((state) => state.maps.currentMapInitialConfig)

  const { initialMapStyleId, initialStyleType } = useMemo(() => {
    const initialMapStyleId = currentMapInitialConfig?.mapStyle.styleType
    const initialStyleType = mapStyles.find((style) => style.id === initialMapStyleId)?.type

    return { initialMapStyleId, initialStyleType }
  }, [mapStyles, currentMapInitialConfig])

  return {
    isLoading,
    mapStyles,
    basemapSettings,
    initialStyleType,
    initialMapStyleId
  }
}

export function useBasemapConfig() {
  return useAppSelector(currentBasemapConfigWithStyleSelector)
}

export function useBasemapSettingsUpdate() {
  const dispatch = useAppDispatch()

  return useMemo(
    () => ({
      setStyleId: (styleId: string) => {
        dispatch(updateBasemapConfig({ styleId }))
      },
      updateBasemapConfig: (newProps: Partial<BasemapConfig>) => {
        dispatch(updateBasemapConfig(newProps))
      }
    }),
    [dispatch]
  )
}

const MAP_TYPES_NO_ATTTRIBUTION = [MapStyleType.GOOGLE_MAPS, MapStyleType.AMAZON]

export function useBasemapAttribution() {
  const { currentMapStyle } = useBasemapConfig()
  if (currentMapStyle?.type === MapStyleType.CUSTOM) {
    return {
      showAttributions: Boolean(currentMapStyle?.customAttribution),
      customAttribution: currentMapStyle?.customAttribution
    }
  }
  if (currentMapStyle && MAP_TYPES_NO_ATTTRIBUTION.includes(currentMapStyle.type)) {
    return {
      showAttributions: false
    }
  }
  return {
    showAttributions: true
  }
}

export function useBasemaps() {
  const { mapStyles, initialMapStyleId } = useBasemapStyles()
  const { setStyleId } = useBasemapSettingsUpdate()
  const { styleId } = useAppSelector(basemapConfigSelector)

  const currentBasemap = useMemo(() => {
    return mapStyles.find((style) => style.id === styleId)
  }, [mapStyles, styleId])

  const displayedMapStyles = useMemo(
    () => mapStyles.filter((style) => !(style.disabled && style.id !== styleId && style.id !== initialMapStyleId)),
    [mapStyles, styleId, initialMapStyleId]
  )
  // Memorize mapStylesByType to get basemaps grouped by provider
  const mapStylesByType = useMemo(() => {
    const mapStylesByType = {}
    displayedMapStyles.forEach((style) => {
      if (!mapStylesByType[style.type]) {
        mapStylesByType[style.type] = [
          {
            id: style.type,
            label: MAP_TYPES[style.type],
            style: {},
            type: 'divider'
          }
        ]
      }
      mapStylesByType[style.type].push(style)
    })

    return mapStylesByType
  }, [displayedMapStyles])

  const changeBasemapStyle = useCallback(
    (value) => {
      const mapStyle = mapStyles.find((style) => style.id === value || style.label === value)?.id
      if (mapStyle) {
        setStyleId(mapStyle)
      }
    },
    [mapStyles, setStyleId]
  )

  return {
    currentBasemapId: styleId,
    currentBasemap,
    mapStyles: displayedMapStyles,
    mapStylesByType,
    changeBasemapStyle
  }
}
